/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  useDispatch,
  useSelector
} from 'react-redux';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import clsx from 'clsx';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {CRUDPermissions, Device, EntityStatus, GenericObject} from '@ion/core/src/types/entities';
import {changeURLPath} from '@ion/core/src/utils/BrowserUtils';
import {
  getDeviceIdentifierFromType,
  isMDTDevice as isValidMDTDevice
} from '@ion/core/src/utils/DeviceUtils';
import Divider from '@ion/dls/src/components/divider/Divider';
import {ContentLoader} from '@ion/dls/src/components/loading/ContentLoader';
import {SnackbarMessage} from '@ion/dls/src/components/snackbars/SnackbarMessage';
import useSnackbar from '@ion/dls/src/components/snackbars/useSnackbar';
import {isSelectedDeviceSCI} from '@ion/dls/src/editors/DeviceEditorUtils';
import DeviceViewDialog from '@ion/dls/src/editors/DeviceViewDialog';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {NewIcon} from '@ion/dls/src/icons/NewIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {
  useScheduleDataSyncOperation
} from '@ion/query/src/hooks/useScheduleDataSyncOperation';
import {useSmartSearchDevice} from '@ion/query/src/hooks/useSmartSearchDevice';
import {showJobCreateEditor} from '../../components/jobs/state';
import {ids} from '../../hooks/useDeviceDeepLink';
import {useDeviceViewRefresh} from '../../hooks/useDeviceViewRefresh';
import {useFilePermissionsAction} from '../../hooks/useFilePermissionsAction';
import {useUrl} from '../../hooks/useUrl';
import {
  deviceDetailSelector,
  devicePermissionsSelector,
  devicesViewSelector
} from '../../store/selectors/devices';
import {AlertHistoryTab} from './AlertHistoryTab';
import CarbTab from './CarbTab';
import {DeviceDetailsTab} from './DeviceDetailsTab';
import {DeviceInfoPanel} from './DeviceInfoPanel';
import {EventsTab} from './EventsTab';
import {FilesTab} from './FilesTab';
import {HealthEventsTab} from './HealthEventsTab';
import {IdReportTab} from './IdReportTab';
import {ManufacturingTab} from './ManufacturingTab';
import {SCISettingsTab} from './SCISettingsTab';
import {hasPermission} from './SCISettingsUtils';
import SentinelAssociationTab, {hasSentinelAssociationTab} from './SentinelAssociationTab';
import {ServicesTab} from './ServicesTab';
import {ShippingWarrantyTab} from './ShippingWarrantyTab';
import {
  auditDevices,
  editAdminDevice,
  loadDeviceDetail,
  moveAdminDevices,
  resetDevicesTab,
  setSecondaryDevice,
  setSentinelTab,
  swapAdminDevice,
  swapAdminDeviceConfirm,
  viewAdminDevice,
  viewDevice
} from './state';
import {deviceSentinelTabSelector} from './state-selectors';
import {DeviceEditorTabs} from './types';
import {useSentinelAssociationNewBadge} from './useSentinelAssociationNewBadge';

const {
  DETAILS,
  FILES,
  SERVICES,
  ID_REPORT,
  HEALTH_EVENTS,
  TELEMETRY_EVENTS,
  SENTINEL_ASSOCIATION,
  CARB,
  ALERT_HISTORY,
  MANUFACTURING,
  SHIPPING,
  SCI_SETTINGS
} = DeviceEditorTabs;

export interface ViewDeviceDisplayOptions {
  explicitReportDate?: boolean;
  refreshDevice?: boolean;
}

export interface ViewDeviceEditorProps extends ViewDeviceDisplayOptions {
  selectedJobId?: (job: number) => void;
  includeEditAction?: boolean;
  isToggleSwitchGrid?: boolean;
  isShowColumnChooserIcon?: boolean;
  isShowRefreshButton?: boolean;
  devicesPermissions?: CRUDPermissions;
  hasCreateJob?: boolean;
  isCtcAdmin?: boolean;
  Features?: GenericObject;
  isVbuFwEnabled?: boolean;
  hasCARBEnabled?: boolean;
  hasTeleCryptEnabled?: boolean;
  hasVinReadFeature?: boolean;
  setIsTaskDeviceClicked?: Function;
  onViewInVehicleManager?: (device: Device) => void;
};

export const viewDeviceStyles = (theme: DLSTheme) => ({
  panelSection : {
    background : dlsColor('white')
  },
  stickyTabs : {
    position        : 'sticky',
    top             : 0,
    zIndex          : 100,
    backgroundColor : dlsColor('grey6')
  },
  customTab : {
    color                  : dlsColor('grey1'),
    letterSpacing          : '1px',
    '& .MuiTabs-indicator' : {
      backgroundColor : dlsColor('aaBlue'),
      height          : '4px'
    },
    '& .MuiTab-root' : {
      ...dlsAlt('typography', 'label1'),
      color        : dlsColor('grey1'),
      fontWeight   : 700,
      minWidth     : 'auto',
      padding      : '0px 10px',
      marginBottom : '-4px',
      transition   : 'color 200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
      '&:hover'    : {
        opacity : 1,
        color   : dlsColor('aaBlue')
      }
    },
    '& .MuiTab-root.Mui-selected' : {
      color     : dlsColor('aaBlue'),
      '&:hover' : {
        color : dlsColor('aaBlue')
      }
    },
    '& .MuiTab-wrapper' : {
      marginBottom : '-10px'
    }
  },
  customDivider : {
    backgroundColor : dlsColor('grey4'),
    marginTop       : -1,
    height          : 1
  },
  contentContainer : {
    width : '100%'
  },
  center : {
    height         : '100%',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  tabRoot : {
    flexDirection       : 'row',
    '& .MuiTab-wrapper' : {
      display       : 'flex !important',
      flexDirection : 'row !important'
    }
  },
  iconWrapper : {
    marginRight : 8
  }
});

/**
 * Display View Device Editor on click on view option from grid menu
 */
export const ViewDeviceEditor = ({
  selectedJobId,
  devicesPermissions,
  hasCreateJob,
  Features,
  setIsTaskDeviceClicked,
  includeEditAction = false,
  refreshDevice = false,
  explicitReportDate = false,
  isVbuFwEnabled = false,
  hasCARBEnabled = false,
  hasTeleCryptEnabled = false,
  hasVinReadFeature = false,
  onViewInVehicleManager,
  isToggleSwitchGrid = false,
  isShowColumnChooserIcon = false,
  isCtcAdmin = false,
  isShowRefreshButton = true
}: ViewDeviceEditorProps) => {
  const editorStyles                  = useExtendStyles(viewDeviceStyles);
  const classes                       = editorStyles();
  const dispatch                      = useDispatch();
  const deviceDetail                  = useSelector(deviceDetailSelector);
  const [activeTab, setActiveTab]     = useState(0);
  const [scriptDesc, setScriptDesc]   = useState('');
  const [gridRefresh, setGridRefresh] = useState(false);
  const hasCarbServiceEnabled         = hasCARBEnabled && (deviceDetail?.features?.CARB === EntityStatus.Enabled);

  const {
    latestIdReport,
    SCIDeviceData,
    isSCIDevice
  } = useSelector(devicesViewSelector);

  const {
    device: populatedDevice,
    refresh: onRefreshDevice
  } = useSmartSearchDevice({id: deviceDetail?.id}, !!deviceDetail?.id);

  const {permissions}    = useSelector(devicePermissionsSelector);
  const isLMU55Device    = deviceDetail?.type?.title.toLowerCase().includes('lmu55');
  const isMDTDevice      = isValidMDTDevice(deviceDetail);
  const deviceIdentifier = getDeviceIdentifierFromType(deviceDetail);
  
  const {getPath, hasQueryParam} = useUrl();
  const refreshRoute             = !isEmpty(ids.filter(id => hasQueryParam(id))?.[0]);
  const hasFilePermissions       = !!useFilePermissionsAction({Features});
  const {process, isProcessing}  = useScheduleDataSyncOperation() || {};
  const {snackbar}               = translate('admin-devices:scheduleDataSync', {returnObjects: true});
  const hasSentinelAssociation   = hasSentinelAssociationTab(deviceDetail);
  const {
    showNewBadge: showSentinelNewBadge,
    onSentinelTabViewed,
    onDialogClose: onSentinelDialogClose
  } = useSentinelAssociationNewBadge(hasSentinelAssociation);
  const hasClickedSentinelColumn = useSelector(deviceSentinelTabSelector);

  useEffect(() => {
    if (hasClickedSentinelColumn) {
      setActiveTab(6);
    }
  }, [hasClickedSentinelColumn]);

  useEffect(() => {
    if (activeTab === SENTINEL_ASSOCIATION && hasSentinelAssociation) {
      onSentinelTabViewed();
    }
  }, [activeTab, hasSentinelAssociation, onSentinelTabViewed]);

  const onCancel = useCallback(() => {
    onSentinelDialogClose();
    if (refreshRoute) {
      changeURLPath(getPath());
    }
    dispatch(viewAdminDevice(null, false));
    dispatch(loadDeviceDetail(null));
    dispatch(setSecondaryDevice(null, false));
    dispatch(setSentinelTab(false));
    dispatch(resetDevicesTab());
  }, [refreshRoute, onSentinelDialogClose]);

  const onEdit = useCallback(() => {
    // Pass null since the edit dialog now pulls in the device information itself.
    dispatch(editAdminDevice(null));
  }, []);

  const onAudit = useCallback(() => {
    dispatch(auditDevices(populatedDevice));
  }, [populatedDevice]);

  const onCreateJob = useCallback(() => {
    dispatch(showJobCreateEditor([populatedDevice], null, true));
  }, [populatedDevice]);

  const onMoveDevice = useCallback(() => {
    dispatch(moveAdminDevices([populatedDevice]));
  }, [populatedDevice]);

  const triggerRefresh = useDeviceViewRefresh();

  const onRefresh = useCallback(() => {
    dispatch(viewDevice(populatedDevice, refreshDevice) as any);
    onRefreshDevice();
    triggerRefresh();
    setGridRefresh(true);
  }, [
    populatedDevice,
    refreshDevice
  ]);

  const onSwap = useCallback(() => {
    if (populatedDevice?.groups?.length || populatedDevice?.asset?.href || isSelectedDeviceSCI(populatedDevice)) {
      dispatch(swapAdminDeviceConfirm(populatedDevice));
    } else {
      dispatch(swapAdminDevice(populatedDevice));
    }
  }, [populatedDevice]);

  const {
    show: showBulkSnackbar,
    message: bulkSnackbarMessage,
    openSnackbar: onComplete,
    dismissSnackbar: dismissBulkSnackbar
  } = useSnackbar();

  const onScheduleDataSync = useCallback(() => {
    process?.({
      id : populatedDevice?.id
    }, {
      onSuccess : () => {
        onComplete(snackbar.success);
      },
      onError : (err) => {
        onComplete(snackbar.error);
        error('Unable to process the provided schedule data sync operation due to: ', err);
      }
    });
  }, [populatedDevice]);

  function handleChange(event, newValue) {
    setActiveTab(newValue);
  };

  const SCIRead = useMemo((): boolean => {
    return hasPermission(permissions, 'read', 'deviceCommand');
  }, [permissions]);

  const SCIUpdate = useMemo((): boolean => {
    return hasPermission(permissions, 'update', 'deviceCommand');
  }, [permissions]);

  const hasSCIPermission = useMemo(() => (
    SCIRead || SCIUpdate
  ), [permissions]);

  const tabs = useMemo(() => translate('admin-devices:view.tabs', {returnObjects: true}), []);

  return (
    <DeviceViewDialog open
      onCloseDialog={onCancel}
      hasCreateJob={hasCreateJob}
      hasFilePermissions={hasFilePermissions}
      devicesPermissions={devicesPermissions}
      includeEditAction={includeEditAction}
      hasSentinelAssociation={hasSentinelAssociation}
      selectedDevice={populatedDevice}
      deviceDetail={deviceDetail}
      onRefresh={onRefresh}
      onEdit={onEdit}
      onSwap={onSwap}
      onScheduleDataSync={onScheduleDataSync}
      onAudit={onAudit}
      onCreateJob={onCreateJob}
      onMoveDevice={onMoveDevice}>
      <div>
        <DeviceInfoPanel device={populatedDevice}
          deviceDetail={deviceDetail}
          latestIdReport={latestIdReport}
          isCtcAdmin={isCtcAdmin}
          explicitReportDate={explicitReportDate} />
      </div>
      <div className={classes.contentContainer}>
        <div>
          <div className={classes.stickyTabs}>
            <Tabs className={classes.customTab}
              value={activeTab}
              onChange={handleChange}
              variant='scrollable'
              TabIndicatorProps={{
                style : {
                  transition : 'none'
                }
              }}>
              <Tab value={DETAILS} label={tabs.deviceDetails} disableRipple />
              <Tab value={FILES} label={tabs.files} disableRipple />
              {!isMDTDevice && (
                <Tab value={SERVICES} label={tabs.services} disableRipple />
              )}
              <Tab value={ID_REPORT} label={tabs.idReport} disableRipple />
              {isLMU55Device && (
                <Tab value={HEALTH_EVENTS} label={tabs.healthEvent} disableRipple />
              )}
              <Tab value={TELEMETRY_EVENTS} label={tabs.events} disableRipple />
              {hasSentinelAssociation && (
                <Tab value={SENTINEL_ASSOCIATION}
                  icon={
                    <Render.If condition={showSentinelNewBadge}>
                      <div className={classes.iconWrapper}>
                        <NewIcon size={IconSize.largeMedium} color={dlsColor('caGreen')} />
                      </div>
                    </Render.If>
                  }
                  className={clsx(classes.tabStyle, classes.tabRoot)}
                  label={tabs.sentinel}
                  disableRipple />
              )}
              {hasCarbServiceEnabled && (
                <Tab value={CARB}
                  icon={
                    <div className={classes.iconWrapper}>
                      <NewIcon size={IconSize.largeMedium} color={dlsColor('caGreen')} />
                    </div>
                  }
                  className={clsx(classes.tabStyle, classes.tabRoot)}
                  label={tabs.carb}
                  disableRipple />
              )}
              <Tab value={ALERT_HISTORY} label={tabs.alertHistory} disableRipple />
              <Tab value={MANUFACTURING} label={tabs.mfgData} disableRipple />
              <Tab value={SHIPPING} label={tabs.shippingWarranty} disableRipple />
              {hasSCIPermission && SCIDeviceData?.sciDeviceSettings && (
                <Tab value={SCI_SETTINGS} label={tabs.sciSettings} disableRipple />
              )}
            </Tabs>
            <Divider className={classes.customDivider} />
          </div>
          <>
            <Render.If condition={activeTab === DETAILS}>
              <DeviceDetailsTab deviceDetail={deviceDetail}
                Features={Features}
                isVbuFwEnabled={isVbuFwEnabled}
                isCurrentDeviceMDT={isMDTDevice}
                devicesPermissions={devicesPermissions}
                hasVinReadFeature={hasVinReadFeature}
                onViewInVehicleManager={onViewInVehicleManager}
                tabIndex={DETAILS}
                device={populatedDevice}
                onSuccess={onRefresh}
                scriptDesc={scriptDesc} />
            </Render.If>

            <Render.If condition={activeTab === FILES}>
              <FilesTab tabIndex={FILES}
                selectedJobId={selectedJobId}
                setGridRefresh={setGridRefresh}
                device={deviceDetail}
                deviceIdentifier={deviceIdentifier}
                gridRefresh={gridRefresh}
                setIsTaskDeviceClicked={setIsTaskDeviceClicked}
                isToggleSwitchGrid={isToggleSwitchGrid}
                isShowColumnChooserIcon={isShowColumnChooserIcon}
                isShowRefreshButton={isShowRefreshButton}
                setScriptDesc={setScriptDesc} />
            </Render.If>

            <Render.If condition={!isMDTDevice && activeTab === SERVICES}>
              <ServicesTab hasTeleCryptEnabled={hasTeleCryptEnabled}
                Features={Features}
                device={populatedDevice}
                isCtcAdmin={isCtcAdmin}
                tabIndex={SERVICES} />
            </Render.If>

            <Render.If condition={activeTab === ID_REPORT}>
              <IdReportTab isMDT={isMDTDevice}
                tabIndex={ID_REPORT} />
            </Render.If>

            <Render.If condition={isLMU55Device && activeTab === HEALTH_EVENTS}>
              <HealthEventsTab tabIndex={HEALTH_EVENTS}
                device={populatedDevice} />
            </Render.If>
            
            <Render.If condition={activeTab === TELEMETRY_EVENTS}>
              <EventsTab tabIndex={TELEMETRY_EVENTS}
                tabName={tabs.events}
                device={deviceDetail} />
            </Render.If>

            <Render.If condition={hasSentinelAssociation &&
              activeTab === SENTINEL_ASSOCIATION
            }>
              <SentinelAssociationTab tabIndex={SENTINEL_ASSOCIATION}
                tabName={tabs.sentinel}
                currentDevice={populatedDevice || deviceDetail}
                deviceIdentifier={deviceIdentifier || deviceDetail?.id} />
            </Render.If>

            <Render.If condition={hasCarbServiceEnabled && activeTab === CARB}>
              <CarbTab tabIndex={CARB}
                tabName={tabs.carb}
                currentDevice={deviceDetail} />
            </Render.If>

            <Render.If condition={activeTab === ALERT_HISTORY}>
              <AlertHistoryTab tabIndex={ALERT_HISTORY}
                device={populatedDevice} />
            </Render.If>

            <Render.If condition={activeTab === MANUFACTURING}>
              <ManufacturingTab isCMF={isMDTDevice}
                deviceIdentifier={deviceIdentifier}
                isMDTDevice={isMDTDevice}
                tabIndex={MANUFACTURING} />
            </Render.If>

            <Render.If condition={activeTab === SHIPPING}>
              <ShippingWarrantyTab tabIndex={SHIPPING}
                deviceIdentifier={deviceIdentifier} />
            </Render.If>

            <Render.If condition={SCIDeviceData?.sciDeviceSettings &&
              activeTab === SCI_SETTINGS
            }>
              <SCISettingsTab SCIDeviceData={SCIDeviceData}
                tabIndex={SCI_SETTINGS}
                noUpdate={!SCIUpdate}
                serialNumber={populatedDevice?.serialNumber}
                hasSciPermission={hasSCIPermission} />
            </Render.If>
          </>
        </div>
        <div className={classes.center}>
          <Render.If condition={
            ((isSCIDevice && !SCIDeviceData) || isProcessing)
          }>
            <ContentLoader />
          </Render.If>
        </div>
        <SnackbarMessage show={showBulkSnackbar}
          message={bulkSnackbarMessage}
          onDismiss={dismissBulkSnackbar} />
      </div>
    </DeviceViewDialog>
  );
};

export default ViewDeviceEditor;
